import {
  loginApi,
  logoutApi,
  googleLoginApi,
  zaloLoginApi,
  refreshTokenApi
} from "@/modules/authentication/api/api-auth";
import { getUserMe, updateUserProfile, UpdateUserProfilePayload } from "@/modules/user/api/user.api";
import { userAction } from "@/modules/user/reducer/user.slice";
import { User } from "@/modules/user/types/user.type";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    email: string,
    password: string,
    rememberMe: boolean
  ) => Promise<{ success: boolean; message: string }>;
  loginWithGoogle: (
    idToken: string,
    rememberMe: boolean
  ) => Promise<{ success: boolean; message: string }>;
  loginWithZalo: (
    oauth_code: string,
    rememberMe: boolean
  ) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  updateUserProfile: (data: UpdateUserProfilePayload) => Promise<{ success: boolean; message: string }>;
  refreshUser: () => Promise<void>;
  refreshToken: () => Promise<{ success: boolean; message: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();

  // Helper function to save tokens
  const saveTokens = useCallback((accessToken: string, refreshToken: string, rememberMe: boolean) => {
    if (rememberMe) {
      localStorage.setItem("auth_token", accessToken);
      localStorage.setItem("refresh_token", refreshToken);
    } else {
      sessionStorage.setItem("auth_token", accessToken);
      sessionStorage.setItem("refresh_token", refreshToken);
    }
  }, []);

  // Helper function to get tokens
  const getTokens = useCallback(() => {
    const accessToken = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
    const refreshToken = localStorage.getItem("refresh_token") || sessionStorage.getItem("refresh_token");
    return { accessToken, refreshToken };
  }, []);

  // Helper function to clear tokens
  const clearTokens = useCallback(() => {
    localStorage.removeItem("auth_token");
    sessionStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    sessionStorage.removeItem("refresh_token");
    localStorage.removeItem("user_data");
  }, []);

  // Check if user is already logged in on app start
  const checkAuthState = useCallback(async () => {
    const { accessToken } = getTokens();
    setIsLoading(true);

    if (accessToken) {
      try {
        // Call getUserMe API to verify token
        const userMe = await getUserMe();
        if (userMe.data) {
          setUser(userMe.data);
          dispatch(userAction.setUserMe(userMe.data));
        }
      } catch (error: unknown) {
        setUser(null);
        clearTokens();
      }
    } else {
      // No token, clear any existing user data
      clearTokens();
      setUser(null);
    }

    setIsLoading(false);
  }, [dispatch, getTokens, clearTokens]);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      const userMe = await getUserMe();
      if (userMe.data) {
        setUser(userMe.data);
        dispatch(userAction.setUserMe(userMe.data));
      }
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  }, [dispatch]);

  // Update user profile
  const handleUpdateUserProfile = useCallback(
    async (data: UpdateUserProfilePayload): Promise<{ success: boolean; message: string }> => {
      try {
        const response = await updateUserProfile(data);
        if (response.success) {
          // Refresh user data after successful update
          await refreshUser();
          return { success: true, message: "Cập nhật thông tin thành công!" };
        } else {
          const errorMessage =
          (response.message as { error?: { message?: string } })?.error
            ?.message || "Đã xảy ra lỗi";
          return { success: false, message: errorMessage };
        }
      } catch (error: unknown) {
        const errorMessage =
          (error as { data?: { error?: { message?: string } } })?.data?.error
            ?.message || "Đã xảy ra lỗi";
        return { success: false, message: errorMessage };
      }
    },
    [refreshUser]
  );

  useEffect(() => {
    checkAuthState();
  }, [checkAuthState]);

  const login = useCallback(
    async (
      email: string,
      password: string,
      rememberMe: boolean
    ): Promise<{ success: boolean; message: string }> => {
      try {
        const response = await loginApi(email, password);
        if (response.success) {
          saveTokens(response.data.accessToken, response.data.refreshToken, rememberMe);
          // Get user data directly after login without calling checkAuthState
          try {
            const userMe = await getUserMe();
            if (userMe.data) {
              setUser(userMe.data);
              dispatch(userAction.setUserMe(userMe.data));
            }
          } catch (error) {
            console.error("Error fetching user data after login:", error);
          }
          return { success: true, message: response.message };
        } else {
          return { success: false, message: response.message };
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Đăng nhập thất bại!";
        return { success: false, message: errorMessage };
      }
    },
    [dispatch, saveTokens]
  );

  const loginWithGoogle = useCallback(
    async (
      idToken: string,
      rememberMe: boolean
    ): Promise<{ success: boolean; message: string }> => {
      try {
        const response = await googleLoginApi(idToken);
        if (response.success) {
          saveTokens(response.data.accessToken, response.data.refreshToken, rememberMe);
          try {
            const userMe = await getUserMe();
            if (userMe.data) {
              setUser(userMe.data);
              dispatch(userAction.setUserMe(userMe.data));
            }
          } catch (error) {
            console.error("Error fetching user data after Google login:", error);
          }
          return { success: true, message: response.message };
        }
        return { success: false, message: response.message };
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Đăng nhập Google thất bại!";
        return { success: false, message: errorMessage };
      }
    },
    [dispatch, saveTokens]
  );

  const loginWithZalo = useCallback(
    async (
      oauth_code: string,
      rememberMe: boolean
    ): Promise<{ success: boolean; message: string }> => {
      try {
        const response = await zaloLoginApi(oauth_code);
        if (response.success) {
          saveTokens(response.data.accessToken, response.data.refreshToken, rememberMe);
          try {
            const userMe = await getUserMe();
            if (userMe.data) {
              setUser(userMe.data);
              dispatch(userAction.setUserMe(userMe.data));
            }
          } catch (error) {
            console.error("Error fetching user data after Zalo login:", error);
          }
          return { success: true, message: response.message };
        }
        return { success: false, message: response.message };
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Đăng nhập Zalo thất bại!";
        return { success: false, message: errorMessage };
      }
    },
    [dispatch, saveTokens]
  );

  const logout = useCallback(async () => {
    try {
      const { refreshToken } = getTokens();
      if (!refreshToken) {
        setUser(null);
        clearTokens();
        return { success: false, message: "Không tìm thấy refresh token" };
      }
      await logoutApi(refreshToken, false);
      setUser(null);
      clearTokens();
      return { success: true, message: "Đăng xuất thành công" };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Đăng xuất thất bại!";
      return { success: false, message: errorMessage };
    }
  }, [clearTokens, getTokens]);

  // Refresh token function
  const refreshToken = useCallback(async (): Promise<{ success: boolean; message: string }> => {
    try {
      const { refreshToken: storedRefreshToken } = getTokens();
      
      if (!storedRefreshToken) {
        return { success: false, message: "Không tìm thấy refresh token" };
      }

      const response = await refreshTokenApi(storedRefreshToken);
      
      if (response.success) {
        // Determine if user chose rememberMe by checking localStorage
        const rememberMe = !!localStorage.getItem("auth_token");
        saveTokens(response.data.accessToken, response.data.refreshToken, rememberMe);
        return { success: true, message: "Làm mới token thành công" };
      } else {
        return { success: false, message: response.message || "Làm mới token thất bại" };
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Làm mới token thất bại!";
      return { success: false, message: errorMessage };
    }
  }, [getTokens, saveTokens]);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      loginWithGoogle,
      loginWithZalo,
      logout,
      updateUserProfile: handleUpdateUserProfile,
      refreshUser,
      refreshToken,
    }),
    [user, isLoading, login, loginWithGoogle, loginWithZalo, logout, handleUpdateUserProfile, refreshUser, refreshToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
